import { useSyncExternalStore } from 'react';
import { REST_PICKER_MAX, REST_PICKER_MIN } from '@heraion/shared';

// The rest the athlete corrects by hand, learned per exercise (#40). The base
// of the NEXT rest on that lift, never a rewrite of the coach's prescription:
// resolveRestSeconds only reads it below the prescribed rest and the superset
// hop. Per device, like the other timer preferences; the storage is injected
// (AsyncStorage on native, localStorage on web).
//
// ⛔ It learns only from NEUTRAL sets. After a set at or near failure the app
// invites a longer rest (restInvite.ts); if that rest taught the base, the base
// would climb on every failure (rest-adattivo.md §7). So learning is armed per
// rest, by the code that arms the rest, and only for a neutral set.
export type KeyValueStore = {
  get: () => Promise<string | null>;
  set: (value: string) => Promise<void>;
};

type LearnedMap = Readonly<Record<string, number>>;

export function createLearnedRestStore(kv: KeyValueStore) {
  let map: LearnedMap = {};
  let armed: { slug: string; learnable: boolean } | null = null;
  const listeners = new Set<() => void>();
  const notify = () => listeners.forEach((fn) => fn());

  const parse = (raw: string | null): LearnedMap => {
    if (!raw) return {};
    try {
      const v: unknown = JSON.parse(raw);
      if (typeof v !== 'object' || v === null) return {};
      const out: Record<string, number> = {};
      for (const [k, n] of Object.entries(v)) {
        if (typeof n === 'number' && n >= REST_PICKER_MIN && n <= REST_PICKER_MAX) {
          out[k] = n;
        }
      }
      return out;
    } catch {
      return {};
    }
  };

  // Every write waits for the first read, so a tap before boot finishes cannot
  // be overwritten by the stored map arriving late.
  const loaded: Promise<void> = kv
    .get()
    .then((raw) => {
      map = { ...parse(raw), ...map };
      notify();
    })
    .catch(() => undefined);

  const persist = (): void => {
    void loaded.then(() => kv.set(JSON.stringify(map)).catch(() => undefined));
  };

  const get = (slug: string | null | undefined): number | undefined =>
    slug ? map[slug] : undefined;

  const clear = (slug: string): void => {
    if (!(slug in map)) return;
    const { [slug]: _gone, ...rest } = map;
    map = rest;
    notify();
    persist();
  };

  /** Called where a rest is armed. `learnable` is false for anything but a
   *  neutral, un-prescribed set of the exercise. */
  const arm = (slug: string | null | undefined, learnable: boolean): void => {
    armed = slug ? { slug, learnable } : null;
  };

  /** The athlete changed the running rest: `totalSec` is the length they set.
   *  Elapsed time is never learned. A rest that is not armed as learnable, or
   *  that was cut to nothing, teaches nothing. */
  const learn = (slug: string | null | undefined, totalSec: number): void => {
    if (!slug || !armed || armed.slug !== slug || !armed.learnable) return;
    if (!Number.isFinite(totalSec)) return;
    const sec = Math.round(totalSec);
    if (sec < REST_PICKER_MIN || sec > REST_PICKER_MAX) return;
    if (map[slug] === sec) return;
    map = { ...map, [slug]: sec };
    notify();
    persist();
  };

  const subscribe = (fn: () => void): (() => void) => {
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  };

  /** The learned rest of one exercise, live. */
  const useLearned = (slug: string | null | undefined): number | undefined =>
    useSyncExternalStore(subscribe, () => get(slug), () => undefined);

  return { get, arm, learn, clear, useLearned };
}
